import { useStore } from "store";
import { useNavigate } from "react-router-dom";

import {
  theme,
  Typography,
  Space,
  Tag,
  Tooltip,
  Flex,
  Button,
  Progress,
} from "antd";
const { Text } = Typography;

import {
  InfoCircleFilled,
  Loading3QuartersOutlined,
  DollarCircleFilled,
  CrownFilled,
  WarningFilled,
  FilePdfOutlined,
  FileTextOutlined,
  FileProtectOutlined,
  CopyOutlined,
  TransactionOutlined,
} from "@ant-design/icons";

import "./style.scss";

import { getFormattedUnixDate } from "shared/utilities";

export const RowItem = ({
  title,
  text,
  vertical = false,
  indent = false,
  hideIfEmpty = false,
  style = null,
}) => {
  if (hideIfEmpty && (!!!text || text === "")) {
    return null;
  }
  return indent ? (
    <Flex justify="space-between">
      <Text style={style} strong>
        {title}:
      </Text>
      <Text style={style}>{text}</Text>
    </Flex>
  ) : (
    <Flex gap={vertical ? 0 : 10} vertical={vertical}>
      <Text style={style} strong>
        {title}:
      </Text>
      <Text style={style}>{text}</Text>
    </Flex>
  );
};

export const TooltipFeatureUnavailable = ({
  requiredPlans,
  tooltipAdditionalText,
  children,
}) => {
  const navigate = useNavigate();

  const planPro = (
    <>
      <CrownFilled className="fg-gold" /> Pro
    </>
  );

  const planBasic = "Básico";

  const availableIn = (
    <>
      {requiredPlans?.includes("pro") && planPro}
      {requiredPlans?.includes("basic") && " ó " + planBasic}
    </>
  );

  return (
    <Tooltip
      overlayStyle={{
        maxWidth: "360px",
      }}
      placement="top"
      title={
        <Space direction="vertical" size={0}>
          {tooltipAdditionalText}
          <Space size={15}>
            <p>Disponible en el plan {availableIn}</p>
            <Button size="small" onClick={() => navigate("/plan")}>
              Ver Planes
            </Button>
          </Space>
        </Space>
      }
    >
      {children}
    </Tooltip>
  );
};

export const EfosTag = () => {
  const navigate = useNavigate();

  return (
    <Tag
      icon={<WarningFilled />}
      color={"orange"}
      style={{ cursor: "pointer" }}
      onClick={() => navigate("/efos")}
    >
      EFOS
    </Tag>
  );
};

export const CfdiTags = ({ item }) => {
  const tags = { cfdi_type: item.cfdi_type, cfdi_status: item.cfdi_status };

  const CFDI_COMPROBANTE_EFECTOS = {
    I: {
      label: "Ingreso",
      backgroundColor: "#F0F5FF",
      color: "#2F54EB",
    },
    E: {
      label: "Egreso",
      backgroundColor: "#FFF2E8",
      color: "#D46B08",
    },
    T: {
      label: "Traslado",
      backgroundColor: "#FFF0F5",
      color: "#A61B4D",
    },
    N: {
      label: "Nómina",
      backgroundColor: "#F9F0FF",
      color: "#722ED1",
    },
    P: {
      label: "Pago",
      backgroundColor: "#F5EFE8",
      color: "#8C5A3C",
    },
  };

  const CFDI_COMPROBANTE_ESTATUS = {
    0: {
      label: "Cancelado",
      backgroundColor: "#FFF1F0",
      color: "#CF1322",
    },
    1: {
      label: "Vigente",
      backgroundColor: "#F6FFED",
      color: "#389E0D",
    },
  };

  const items = [];
  Object.entries(tags).forEach(([type, val]) => {
    let label = val;
    let backgroundColor = "gray";
    let color = "black";

    if (type === "cfdi_type") {
      label = CFDI_COMPROBANTE_EFECTOS[val]["label"];
      backgroundColor = CFDI_COMPROBANTE_EFECTOS[val]["backgroundColor"];
      color = CFDI_COMPROBANTE_EFECTOS[val]["color"];
    } else if (type === "cfdi_status") {
      label = CFDI_COMPROBANTE_ESTATUS[val]["label"];
      backgroundColor = CFDI_COMPROBANTE_ESTATUS[val]["backgroundColor"];
      color = CFDI_COMPROBANTE_ESTATUS[val]["color"];
    }

    items.push(
      type === "cfdi_status" && item.cfdi_status === 0 ? (
        <Tooltip
          placement="top"
          title={`Cancelado el ${getFormattedUnixDate(item.date_canceled_at)}`}
          overlayStyle={{ maxWidth: "500px" }}
          key={label}
        >
          <Tag
            key={label}
            icon={<InfoCircleFilled />}
            style={{
              cursor: "help",
              backgroundColor: backgroundColor,
              color: color,
              borderColor: color,
              borderWidth: "1px",
              borderStyle: "solid",
            }}
          >
            {label}
          </Tag>
        </Tooltip>
      ) : (
        <Tag
          key={label}
          style={{
            backgroundColor: backgroundColor,
            color: color,
            borderColor: color,
            borderWidth: "1px",
            borderStyle: "solid",
          }}
        >
          {label}
        </Tag>
      ),
    );
  });

  return <Flex>{items}</Flex>;
};

export const CfdiAmount = ({
  item,
  amountType = "total",
  includeCurrency = true,
  style = null,
  align = "center",
}) => {
  const { token: theme_token } = theme.useToken();

  const user = useStore.getState().user;
  const activeRfc = useStore.getState().activeRfc;
  const active_rfc_obj = user.rfcs[activeRfc];

  const renderValue = (amount, currency, style) => {
    const contents = (
      <Flex gap={8} style={{ flexShrink: 0 }}>
        <Space>
          <Text style={style || { fontWeight: "bold", fontSize: "15px" }}>
            <span style={{ whiteSpace: "nowrap" }}>
              $ {amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}
            </span>
          </Text>
        </Space>
        <Space>
          {includeCurrency ? tagCurrency(currency, align, item) : null}
        </Space>
      </Flex>
    );

    return (
      <Flex
        justify={
          align === "center"
            ? "center"
            : align === "left"
              ? "flex-start"
              : "flex-end"
        }
        gap={5}
      >
        {contents}
      </Flex>
    );
  };

  const tagCurrency = (currency, align = null, item = null) => {
    const marginRightTag = align === "right" ? "0px" : null;

    if (active_rfc_obj.plan_name === "free") {
      return (
        <TooltipFeatureUnavailable
          requiredPlans={["basic", "pro"]}
          tooltipAdditionalText={
            <p>
              <b>
                <u>Moneda no disponible</u>
              </b>
              <br />
              La información de la moneda no está disponible en este plan.
            </p>
          }
          children={
            <Tag
              color="grey"
              key="SP"
              icon={<InfoCircleFilled />}
              style={{
                marginRight: marginRightTag,
                fontWeight: "normal",
                cursor: "help",
              }}
            ></Tag>
          }
        />
      );
    } else if (currency) {
      const isBaseCurrency = ["mxn", "xxx"].includes(currency.toLowerCase());

      const tagCurrencyBase = (
        <Tag
          color="blue"
          key={currency}
          icon={isBaseCurrency ? null : <TransactionOutlined />}
          style={{
            marginRight: marginRightTag,
            fontWeight: "normal",
            cursor: isBaseCurrency ? null : "help",
          }}
        >
          {currency}
        </Tag>
      );

      const tooltipContent = (
        <Flex vertical gap={5}>
          <RowItem
            title="Tipo de cambio MXN"
            text={`$ ${item.total.fx_rate.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 6,
            })}`}
            style={{ color: "white" }}
            indent
          />
          <RowItem
            title="Equivalencia MXN"
            text={`$ ${(
              (item.total.fx_rate * item.total.cents) /
              100
            ).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`}
            style={{ color: "white" }}
            indent
          />
        </Flex>
      );

      return isBaseCurrency ? (
        tagCurrencyBase
      ) : (
        <Tooltip
          title={tooltipContent}
          overlayStyle={{ minWidth: "250px", maxWidth: "600px" }}
        >
          {tagCurrencyBase}
        </Tooltip>
      );
    } else if (item?.cfdi_files_status === "canceled_no_xml") {
      return (
        <Tooltip
          placement="top"
          title={
            <p>
              <b>
                <u>Sin Moneda</u>
              </b>
              <br />
              No tenemos información sobre la moneda de este monto.
            </p>
          }
        >
          <Tag
            color="grey"
            icon={<InfoCircleFilled />}
            style={{
              marginRight: marginRightTag,
              fontWeight: "normal",
              cursor: "help",
            }}
          ></Tag>
        </Tooltip>
      );
    } else {
      return (
        <Tooltip
          placement="top"
          title={
            <p>
              <b>
                <u>Cargando Información</u>
              </b>
              <br />
              La información de este CFDI se está cargando.
            </p>
          }
        >
          <Tag
            color={theme_token.colorBgSecondary}
            style={{
              marginRight: marginRightTag,
              padding: "1px 10px",
              fontWeight: "normal",
              cursor: "help",
              borderStyle: "solid",
              borderWidth: "0.5px",
              borderColor: theme_token.colorSecondary,
            }}
          >
            <Loading3QuartersOutlined
              spin={true}
              style={{ color: theme_token.colorSecondary }}
            />
          </Tag>
        </Tooltip>
      );
    }
  };

  const labelPagos = (item) => {
    if (active_rfc_obj.plan_name === "free") {
      return (
        <TooltipFeatureUnavailable
          requiredPlans={["basic", "pro"]}
          tooltipAdditionalText={
            <p>
              <b>
                <u>Pagos no disponibles</u>
              </b>
              <br />
              La información de los pagos no está disponible en este plan.
            </p>
          }
          children={
            <Tag
              color="grey"
              key="SP"
              icon={<InfoCircleFilled />}
              style={{ fontWeight: "normal", cursor: "help" }}
            >
              Pagos no disponibles
            </Tag>
          }
        />
      );
    } else if (item.cfdi_files_status === "canceled_no_xml") {
      return (
        <Tooltip
          placement="top"
          title={
            <p>
              <b>
                <u>Sin Pagos</u>
              </b>
              <br />
              No tenemos información sobre los pagos de este CFDI.
            </p>
          }
        >
          <Tag
            color="grey"
            key="SP"
            icon={<InfoCircleFilled />}
            style={{ fontWeight: "normal", cursor: "help" }}
          >
            {"Sin Pagos"}
          </Tag>
        </Tooltip>
      );
    } else if (!!!item.payments || item.payments.length === 0) {
      return (
        <Tooltip
          placement="top"
          title={
            <p>
              <b>
                <u>Cargando Información</u>
              </b>
              <br />
              La información de este CFDI se está cargando.
            </p>
          }
        >
          <Tag
            color={theme_token.colorBgSecondary}
            key="SP"
            style={{
              padding: "1px 10px",
              fontWeight: "normal",
              cursor: "help",
              borderStyle: "solid",
              borderWidth: "0.5px",
              borderColor: theme_token.colorSecondary,
            }}
          >
            <Loading3QuartersOutlined
              spin={true}
              style={{ color: theme_token.colorSecondary }}
            />
          </Tag>
        </Tooltip>
      );
    } else {
      const amount = item.payments_total?.cents / 100;
      const currency = item.payments_total?.currency;
      const tooltip_title = item.payments.length === 1 ? "Pago" : "Pagos";

      return (
        <Flex justify={align === "right" ? "flex-end" : "flex-start"}>
          <Space>
            <Tooltip
              placement="bottom"
              title={`${item.payments.length} ${tooltip_title}`}
            >
              <Tag
                color="purple"
                key="multi-payments"
                icon={<DollarCircleFilled />}
                style={{ fontWeight: "normal", cursor: "help" }}
              >
                {item.payments.length}
              </Tag>
            </Tooltip>
          </Space>
          {renderValue(amount, currency, style)}
        </Flex>
      );
    }
  };

  if (item?.cfdi_type === "P") {
    return labelPagos(item);
  } else {
    let amount,
      currency = null;
    if (amountType === "discount") {
      if (!!!item.discount) {
        return null;
      }
      amount = item.discount.cents / 100;
      currency = item.discount.currency;
    } else if (amountType === "unit_price") {
      amount = item.unit_price?.cents / 100;
      currency = item.unit_price?.currency;
    } else if (amountType === "generic") {
      amount = item / 100;
    } else {
      amount = item.total.cents / 100;
      currency = item.total.currency;
    }

    return renderValue(amount, currency, style);
  }
};

export const CfdiLink = ({ id_cfdi, id_cfdi_url, setItem }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (id_cfdi === id_cfdi_url) {
      return;
    }
    setItem(null);
    navigate(`/cfdis/${id_cfdi}`);
  };

  return (
    <Flex className="cfdi-tag-container" gap={0} align="center">
      <Tag
        className="cfdi-main-tag"
        style={{ cursor: "pointer" }}
        onClick={() => handleClick()}
      >
        <Flex gap={5}>
          <FileProtectOutlined />
          <Text style={{ fontSize: "12px", fontFamily: "monospace" }}>
            {id_cfdi}
          </Text>
        </Flex>
      </Tag>
      <Tooltip title="Copiar el Folio Fiscal">
        <Tag
          className="cfdi-copy-tag"
          style={{
            cursor: "pointer",
            width: 22,
            height: 22,
            padding: 0,
            borderRadius: "50%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginLeft: "-2px",
          }}
          onClick={() => {
            navigator.clipboard.writeText(id_cfdi);
          }}
        >
          <CopyOutlined style={{ fontSize: "11px" }} />
        </Tag>
      </Tooltip>
    </Flex>
  );
};

export const CfdiPaymentProgress = ({ payments, credits, total }) => {
  const percent = Math.floor(((payments - credits) / total) * 100);

  const tooltipSummary = (
    <Flex vertical gap={5}>
      <RowItem
        title="Avance de pago"
        text={`${percent}%`}
        indent
        style={{ color: "white" }}
      />
      <RowItem
        title="Pagos"
        text={
          <CfdiAmount
            item={payments}
            amountType="generic"
            includeCurrency={false}
            style={{ color: "white" }}
          />
        }
        indent
        style={{ color: "white" }}
      />
      <RowItem
        title="Créditos"
        text={
          <CfdiAmount
            item={credits}
            amountType="generic"
            includeCurrency={false}
            style={{ color: "white" }}
          />
        }
        indent
        style={{ color: "white" }}
      />
      <RowItem
        title="Pendiente"
        text={
          <CfdiAmount
            item={total + credits - payments}
            amountType="generic"
            includeCurrency={false}
            style={{ color: "white" }}
          />
        }
        indent
        style={{ color: "white" }}
      />
    </Flex>
  );

  return (
    <Tooltip
      title={tooltipSummary}
      placement="bottom"
      overlayStyle={{ minWidth: "200px" }}
    >
      <Progress
        type="circle"
        size={35}
        showInfo={true}
        percent={percent}
        strokeWidth={10}
        strokeColor={{
          "0%": percent >= 100 ? "#00750A" : "#68a36d",
          "100%": "#00750A",
        }}
        style={{ cursor: "help" }}
      />
    </Tooltip>
  );
};

export const CfdiFileButton = ({ type, file_url }) => {
  const icon = type === "pdf" ? <FilePdfOutlined /> : <FileTextOutlined />;
  const title = type === "pdf" ? "PDF" : "XML";
  return (
    <Button
      type="primary"
      icon={icon}
      size="small"
      onClick={() => window.open(file_url, "_blank")}
    >
      {title}
    </Button>
  );
};
